import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphScopes, backendScopes } from '@/config/authConfig';

export const useAuth = () => {
  const { instance, accounts } = useMsal();

  const getAccount = (): AccountInfo | null => {
    return accounts[0] || null;
  };

  const getAccessToken = async (forBackend: boolean = true): Promise<string> => {
    const account = getAccount();
    if (!account) {
      throw new Error('No active account. Please sign in.');
    }

    const scopes = forBackend ? backendScopes.apiRequest.scopes : graphScopes.profileRequest.scopes;

    try {
      // Try silent token acquisition first
      const response = await instance.acquireTokenSilent({
        scopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // If silent acquisition fails, use redirect (works on mobile)
        await instance.acquireTokenRedirect({
          scopes,
          account,
        });
        // Note: Page will redirect - execution won't continue past this point
        throw new Error('Redirecting for authentication...');
      }
      throw error;
    }
  };

  const login = () => {
    // Use redirect instead of popup for mobile compatibility
    instance.loginRedirect(graphScopes.loginRequest);
  };

  const logout = () => {
    const account = getAccount();
    if (account) {
      // Use redirect instead of popup for mobile compatibility
      instance.logoutRedirect({ 
        account,
        postLogoutRedirectUri: window.location.origin 
      });
    }
  };

  return {
    isAuthenticated: accounts.length > 0,
    account: getAccount(),
    getAccessToken,
    login,
    logout,
  };
};
